import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import { grey } from '@mui/material/colors'
import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { getMetadataRoutes } from 'data/route/routeMetadataSelector'
import { selectOptimizationJobContext } from 'slices/route/persistentOptimizationJobsSlice'
import T from 'T'
import { ROUTE_OPTIMIZATION_SCOPE } from 'types/api/route/routeOptimizationJob'
import { NOTIFICATION_SOURCE } from 'types/modules/notification-center/notification'
import { formatDateToFEFormatDateFns } from 'utils/date'

import type { Notification } from 'types/modules/notification-center/notification'

export interface NotificationFact {
  key: string
  label: string
  icon?: React.ReactElement
  /** Renders the chip's leading avatar in the route's own colour, so the board and the inbox agree. */
  swatch?: string
  /** Master carries the accent because it is the wider-reaching of the two. */
  isAccent?: boolean
}

interface RouteMeta {
  id: string
  color?: string
}

/**
 * The few facts that say which one of these a notification is about, keyed by producer. The bell, the
 * toast and the inbox all read this list, so none of them can describe a run differently.
 */
export const useNotificationFacts = (notification: Notification): NotificationFact[] => {
  const remembered = useSelector(selectOptimizationJobContext(notification.payload?.jobId ?? null))
  const routes = useSelector(getMetadataRoutes, shallowEqual) as ReadonlyArray<RouteMeta>

  if (notification.source !== NOTIFICATION_SOURCE.ROUTE_OPTIMIZATION) return []

  const scope = notification.payload?.scope ?? (remembered?.isMasterRun ? ROUTE_OPTIMIZATION_SCOPE.MASTER : undefined)
  const serviceDate = notification.payload?.serviceDate ?? remembered?.serviceDate
  const routeId = notification.payload?.routeId ?? notification.entityId
  const isMaster = scope === ROUTE_OPTIMIZATION_SCOPE.MASTER

  const facts: NotificationFact[] = []

  if (notification.entityLabel) {
    // The initials come from the label, so the avatar still reads before the route metadata lands.
    const routeColor = routes.find(route => route.id === routeId)?.color
    facts.push({ key: 'route', label: notification.entityLabel, swatch: routeColor || grey[500] })
  }

  if (scope) {
    facts.push({
      key: 'scope',
      label: isMaster ? T.MASTER : T.DAILY,
      icon: isMaster ? <AccountTreeOutlinedIcon /> : <EventOutlinedIcon />,
      isAccent: isMaster,
    })
  }

  if (serviceDate) {
    facts.push({ key: 'serviceDate', label: formatDateToFEFormatDateFns(new Date(`${serviceDate}T00:00:00`)) })
  }

  return facts
}
